import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  OverlayTrigger,
  Tooltip,
  Alert,
  Spinner,
  Dropdown,
  Modal
} from "react-bootstrap";
import { IconButton } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CustomDataGrid from "../components/CustomDataGrid";
import { createUser, fetchUsersFromDB, sendEmail, editUserPartners } from "../utils/api";
import { validateForm, formatPhoneNumber } from "../utils/PreviewUtils";
import {
  existingUsersGridColumns,
  existingUsersRowMapping,
  generateGridRows,
  generateGridColumns,
  usersGridOptionalColumns
} from "../utils/PageUtils";
import PageHelmet from "../components/PageHelmet";
import { availablePartners, availablePlans } from "../constants";

function CreateNewUser({ userRole, bearerToken }) {
  const [statusMessage, setStatusMessage] = useState(null);
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [existingUsers, setExistingUsers] = useState([]);
  const [usersLength, setUsersLength] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  const [refreshGrid, setRefreshGrid] = useState(true);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [emailSuccess, setEmailSuccess] = useState(null);
  const [formValues, setFormValues] = useState({
    organization: "",
    user_name: "",
    email: "",
    phone: "",
    countryCode: "+1",
    plan_name: '',
    partners: []
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editedPartners, setEditedPartners] = useState([]);
  const [editLoading, setEditLoading] = useState(false);
  const [editStatusMessage, setEditStatusMessage] = useState(null);
  const [isEditError, setIsEditError] = useState(false);
  const newUserFields = [
    {
      name: "organization",
      label: "Organization Name",
      tooltip: 'Name of the Organization/business.',
      placeholder: "For ex: Apple inc.",
    },
    {
      name: 'user_name',
      label: 'User Name',
      tooltip: 'Name of the User.',
      placeholder: 'For ex: John Doe',
    },
    {
      name: "email",
      label: "Email Address",
      tooltip: 'Valid email of User for receiving credentials.',
      placeholder: "For ex: name@example.com",
    },
  ];

  useEffect(() => {
    const fetchUserCredentials = async () => {
      setUsersLoading(true);
      setError(null);
      if (!bearerToken) {
        return;
      }
      try {
        const response = await fetchUsersFromDB(bearerToken);

        if (response.status === 200) {
          const data = response.data;
          if (data.count > 0) {
            setUsersLength(data.count);
            const sortedUsers = data.users.sort((a, b) => new Date(b.created) - new Date(a.created));
            setExistingUsers(sortedUsers);
          } else {
            return [];
          }
        } else if (response.status === 404) {
          setError("No user credentials were returned by the API");
          return [];
        } else if (response.status === 500) {
          return [];
        }
      } catch (error) {
        return [];
      } finally {
        setUsersLoading(false);
        if (refreshGrid) {
          setRefreshGrid(false);
        }
      }
    };
    if (refreshGrid) {
      fetchUserCredentials();
    }
  }, [refreshGrid]);

  const handleInputChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;

    setFormValues((prevValues) => {
      if (name === "phone") {
        const formattedPhone = formatPhoneNumber(value);
        return {
          ...prevValues,
          phone: formattedPhone,
        };
      } else {
        return {
          ...prevValues,
          [name]: value,
        };
      }
    });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatusMessage(null);
    setIsError(false);
    setEmailSuccess(null);
    setEmailError(null);

    const { organization, user_name, email, phone, countryCode, plan_name, partners } = formValues;
    const trimmedOrganization = String(organization || "").trim();
    const trimmedEmail = String(email || "").trim();
    const trimmedUserName = String(user_name || "").trim()
    const fullPhone = `${countryCode}${phone.replace(/\D/g, "")}`;
    const { isValid, errorMessage } = validateForm(
      trimmedOrganization,
      fullPhone,
      trimmedEmail,
      trimmedUserName,
      plan_name
    );

    if (!isValid) {
      setStatusMessage(errorMessage);
      setIsError(true);
      setLoading(false);
      return;
    }
    try {
      const payload = {
        organization: trimmedOrganization,
        user_name: trimmedUserName,
        email: trimmedEmail,
        phone: fullPhone,
        plan_name,
      };

      if (partners.length > 0) {
        payload.partners = partners;
      }

      const response = await createUser(payload, bearerToken);
      setStatusMessage(response.message);
      if (response.success) {
        setRefreshGrid(true);
        setFormValues({
          organization: "",
          user_name: "",
          email: "",
          phone: "",
          plan_name: '',
          countryCode: "+1",
          partners: []
        });
      }
      setIsError(!response.success);
    } catch (error) {
      setStatusMessage("An error occurred while creating the user.");
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const existingUsersRows = generateGridRows(
    existingUsers,
    existingUsersRowMapping
  );
  const existingUsersColumns = generateGridColumns(
    existingUsersGridColumns,
    "user_name",
    undefined,
    undefined,
    usersGridOptionalColumns
  );

  const handleRowClick = (e) => {
    setEmailError(null)
    setEmailSuccess(null)
    setStatusMessage(null)
    if (selectedRowId === e.id) {
      setSelectedRowId(null)
      setSelectedUser(null)
    } else {
      setSelectedRowId(e.id)
      setSelectedUser(existingUsers.find(user => user.email === e.row.email))
    }

  }
  const handleResendEmail = async (selectedUser) => {
    setEmailError(null)
    setEmailSuccess(null)
    setStatusMessage(null)
    if (!selectedUser) {
      setEmailError('Please select a user to resend the email.')
      return;
    }
    const { email, user_id, confirmation_status } = selectedUser;
    try {
      if (!email || !user_id || !confirmation_status) {
        setEmailError('Selected user does not have valid attributes to complete this request.')
        return;
      }
      if (confirmation_status === 'Yes') {
        setEmailError(`The selected user ${email} is already confirmed. No need to resend the email.`)
        return;
      }
      const status = await sendEmail(bearerToken, email, user_id);
      if (status.success) {
        setEmailSuccess(status.message)
        setSelectedRowId(null)
        setSelectedUser(null)
      } else {
        setEmailError(status.message)
      }
    } catch (error) {
      setEmailError('An error occurred while resending the email.')
      return;
    }
  }

  const handleEditUser = (selectedUser) => {
    setEmailError(null);
    setEmailSuccess(null);
    setStatusMessage(null)
    setEditStatusMessage(null)
    setIsError(false)
    if (!selectedUser) {
      setEmailError('Please select a user to edit.');
      return;
    }
    setEditingUser(selectedUser);

    const latestUserData = existingUsers.find(u => u.user_id === selectedUser.user_id) || selectedUser;
    const partners = latestUserData.vendors || [];

    setEditedPartners(partners);
    setIsEditModalOpen(true);
  };

  const handleEditPartnersSubmit = async () => {
    setEditLoading(true);
    setEditStatusMessage(null);
    setIsEditError(false);

    try {
      const response = await editUserPartners(
        bearerToken,
        editingUser.user_id,
        editedPartners
      );

      setEditStatusMessage(response.message);
      setIsEditError(!response.success);

      if (response.success) {
        setRefreshGrid(true);
      }
    } catch (error) {
      setEditStatusMessage("An error occurred while updating partners.");
      setIsEditError(true);
    } finally {
      setEditLoading(false);
    }
  };
  return userRole === "admin" ? (
    <div>
      <PageHelmet
        title="Create User"
        description="Create a user account."
        url={`${process.env.REACT_APP_REDIRECT_URI}/users`}
      />
      <div className="user-container">
        <h2 className="create-user-heading">Create user</h2>
        <Form className="user-form-layout" onSubmit={handleSubmit}>
          {newUserFields.map((field) => (
            <Form.Group
              controlId={`form-${field.name}`}
              className="form-group-custom"
              key={field.name}
            >
              <div className="modal-form-field" key={field.name}>
                <Form.Label className="custom-label">
                  {field.label}
                  <span style={{ color: 'red', marginLeft: 3 }}>*</span>
                </Form.Label>
                <OverlayTrigger

                  placement="top"
                  overlay={<Tooltip>{field.tooltip}</Tooltip>}
                >
                  <IconButton tabIndex={-1}>
                    <HelpOutlineIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </OverlayTrigger>
              </div>
              <Form.Control
                type="string"
                name={field.name}
                placeholder={field.placeholder}
                onChange={handleInputChange}
                value={formValues[field.name]}
                className="custom-placeholder"
              />
            </Form.Group>
          ))}
          <Form.Group
            className="form-group-custom"
            controlId="form-countryCode-phone"
          >
            <div className="modal-form-field">
              <Form.Label className="custom-label">
                Mobile Phone
                <span style={{ color: 'red', marginLeft: 3 }}>*</span>
              </Form.Label>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    Valid phone number of User capable of receiving Text/SMS.
                  </Tooltip>
                }
              >
                <IconButton tabIndex={-1}>
                  <HelpOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </OverlayTrigger>
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              <Form.Control
                type="text"
                name="countryCode"
                placeholder="+1"
                value={formValues.countryCode}
                onChange={handleInputChange}
                style={{ maxWidth: "80px" }}
                className="custom-placeholder"
              />
              <Form.Control
                type="text"
                name="phone"
                placeholder="123-456-7890"
                value={formValues.phone}
                onChange={handleInputChange}
                className="custom-placeholder"
              />
            </div>
          </Form.Group>

          <Form.Group className="form-group-custom" controlId="form-planName">
            <div className="modal-form-field">
              <Form.Label className="custom-label">
                Subscription Plan
                <span style={{ color: 'red', marginLeft: 3 }}>*</span>
              </Form.Label>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    Choose the Subscription plan for the new User.
                  </Tooltip>
                }
              >
                <IconButton tabIndex={-1}>
                  <HelpOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </OverlayTrigger>
            </div>

            <Form.Select
              name="plan_name"
              value={formValues.plan_name}
              onChange={handleInputChange}
              className="custom-placeholder"
            >
              <option value="" disabled hidden>Choose a subscription plan</option>
              {availablePlans.map((plan) => (
                <option key={plan} value={plan}>
                  {plan}
                </option>
              ))}
            </Form.Select>

          </Form.Group>
          <Form.Group className="form-group-custom" controlId="form-partners">
            <div className="modal-form-field">
              <Form.Label className="custom-label">
                Partners
              </Form.Label>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    Choose one or more partners for the new user.
                  </Tooltip>
                }
              >
                <IconButton tabIndex={-1}>
                  <HelpOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </OverlayTrigger>
            </div>

            <Dropdown className="w-100" autoClose="outside">
              <Dropdown.Toggle
                id="form-partners"
                variant="light"
                className="w-100 text-start custom-placeholder form-select multi-dropdown"
              >
                <span className="ellipsis-text">
                  {formValues.partners.length > 0
                    ? formValues.partners.join(", ")
                    : "Choose partners"}
                </span>
              </Dropdown.Toggle>

              <Dropdown.Menu className="w-100 dropdown-scroll">
                {availablePartners.map((partner) => (
                  <div key={partner} className="px-3 py-1 dropdown-item" >
                    <Form.Check
                      type="checkbox"
                      id={`check-${partner}`}
                      label={partner}
                      checked={formValues.partners.includes(partner)}
                      onChange={() => {
                        setFormValues((prev) => {
                          const { partners } = prev;
                          const updatedPartners = partners.includes(partner)
                            ? partners.filter((p) => p !== partner)
                            : [...partners, partner];

                          return {
                            ...prev,
                            partners: updatedPartners,
                          };
                        });
                      }}
                    />
                  </div>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>

          {statusMessage && (
            <div className="message-container">
              <Alert
                variant={isError ? "danger" : "success"}
                className="status-message"
                style={{ whiteSpace: "pre-wrap" }}
              >
                {statusMessage}
              </Alert>
            </div>
          )}

          <div style={{ textAlign: "center" }}>
            <Button variant="dark" type="submit" className="create-button">
              {loading ? "Creating..." : "Create User"}
            </Button>
          </div>
        </Form>

      </div>
      {usersLoading ? (
        <div className="loading-container">
          <Spinner animation="border" role="status" />
        </div>
      ) : (
        <div className="existing-grid">
          <h1 className="user-heading">Existing users</h1>
          {emailError && (
            <div className="message-container">
              <Alert variant='danger'>{emailError}</Alert>
            </div>
          )}
          {emailSuccess && (
            <div className="message-container">
              <Alert variant='success'>{emailSuccess}</Alert>
            </div>
          )}
          {error ? (
            <div className="message-container">
              <Alert variant="danger">{error}</Alert>
            </div>
          ) : (
            <CustomDataGrid
              rows={existingUsersRows}
              columns={existingUsersColumns}
              totalRows={usersLength}
              onRowClick={handleRowClick}
              selectedRowId={selectedRowId}
              toolbarProps={{
                showResendEmail: true,
                onResendEmail: handleResendEmail,
                selectedRow: selectedUser,
                showEditUser: true,
                onEditUser: handleEditUser,
              }}
            />
          )}
        </div>
      )}

      {/* Edit Partners Modal */}
      <Modal show={isEditModalOpen} onHide={() => {
        setIsEditModalOpen(false);
        setEditStatusMessage(null);
        setIsEditError(false);
      }} centered>
        <Modal.Header>
          <Modal.Title>Edit User – {editingUser?.user_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="form-group-custom">
            <div className="modal-form-field">
              <Form.Label className="custom-label">Partners</Form.Label>
            </div>

            <Dropdown className="w-100" autoClose="outside">
              <Dropdown.Toggle
                id="edit-form-partners"
                variant="light"
                className="w-100 text-start custom-placeholder form-select multi-dropdown"
              >
                <span className="ellipsis-text">
                  {editedPartners.length > 0
                    ? editedPartners.join(", ")
                    : "Choose partners"}
                </span>
              </Dropdown.Toggle>

              <Dropdown.Menu className="w-100 dropdown-scroll">
                {availablePartners.map((partner) => (
                  <div key={partner} className="px-3 py-1 dropdown-item" >
                    <Form.Check
                      type="checkbox"
                      id={`edit-check-${partner}`}
                      label={partner}
                      checked={editedPartners.includes(partner)}
                      onChange={() => {
                        setEditedPartners((prev) =>
                          prev.includes(partner)
                            ? prev.filter((p) => p !== partner)
                            : [...prev, partner]
                        );
                      }}
                    />
                  </div>
                ))}
                {availablePartners.length === 0 && <Dropdown.Item disabled>No partners available.</Dropdown.Item>}
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>

          {editStatusMessage && (
            <div className="message-container mt-3">
              <Alert
                variant={isEditError ? "danger" : "success"}
                className="status-message"
                style={{ whiteSpace: "pre-wrap" }}
              >
                {editStatusMessage}
              </Alert>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => setIsEditModalOpen(false)} disabled={editLoading}>
            Cancel
          </Button>
          <Button variant="dark" onClick={handleEditPartnersSubmit} disabled={editLoading}>
            {editLoading ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  ) : (
    <div style={{ textAlign: "center", marginTop: "100px" }}>
      <h1>404 - Page Not Found</h1>
      <br />
      <p>The page you're looking for doesn't exist .</p>
    </div>
  );
}

export default CreateNewUser;
